import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, Self } from '@angular/core';
import { defer, merge, of, EMPTY } from 'rxjs';
import { FileManagerTypeEnum } from '../services/file-manager-api';
import { DialogService } from '../../dialogs/dialog.service';
import { AlertDublicateFileComponent, FileOperation } from '../../dialogs/alert-dublicate-file/alert-dublicate-file.component';
import { FmUploaderService } from './fm-uploader.service';
import { NgOnDestroyService } from '../services/ng-on-destroy.service';
import { takeUntil, catchError, mergeMap, concatMap, tap, map, share, switchMap } from 'rxjs/operators';
import { FilesListApiService } from '../../typescript-angular-client/api/fileList/filesList.service';
import { ErrorHandlingService } from '../../http/error-handling.service';
import { DialogCloseReason, IDialogOnClose } from '../../dialogs';
import { FileUploadError, FileUploadInfo } from '../models/FileUploadError';
import { FileModel } from '../../typescript-angular-client/model/fileModel';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'file-uploader',
  templateUrl: './file-uploader.html',
  styleUrls: ['file-uploader.scss'],
  providers: [NgOnDestroyService]
})
export class FileUploaderComponent implements OnInit {
  @Input() currentFolder: string;
  @Input() fileListPageSize: number;
  @Input() accept: string = 'image/*';
  @Input() maxFileSize = Math.pow(1024, 2) * 2;
  @Input() dialogType: FileModel.FileTypeEnum = FileModel.FileTypeEnum.IMAGES;

  @Output() filesUploaded = new EventEmitter();
  @Output() progressDataEmit = new EventEmitter();

  @ViewChild('file', {static: false}) file: ElementRef<HTMLInputElement>;

  public FileManagerTypeEnum = FileManagerTypeEnum;
  private files4upload: File[] = [];
  private currentResolve: any;
  private currentReject: any;
  private applyAllOperation: IDialogOnClose;

  constructor(
    private dialogService: DialogService,
    private fmUploader: FmUploaderService,
    private listApiService: FilesListApiService,
    private errorHandler: ErrorHandlingService,
    private translate: TranslateService,
    @Self() private ngOnDestroy$: NgOnDestroyService
  ) { }

  ngOnInit(): void {
    if (this.dialogType === FileModel.FileTypeEnum.DOCUMENTS) {
      this.accept = '*.*';
    }
  }

  public clickUploadBtn = (event: MouseEvent): void => {
    event.preventDefault();
    const input = this.file.nativeElement;
    if (input !== null && typeof input !== 'undefined') {
      input.click();
    }
  }

  public onFilesAdded = () => {
    const uploadedFiles = this.file.nativeElement.files;
    const files: File[] = [];
    for (const key in uploadedFiles) {
      if (!isNaN(+key)) {
        files.push(uploadedFiles[key]);
      }
    }

    this.file.nativeElement.value = '';

    files.forEach(file => {
      if (this.validate(file)) {
        this.files4upload.push(file);
      }
    });

    if (this.files4upload.length <= 0 && this.fmUploader.errors.length > 0) {
      this.fmUploader.showErrors();
      return;
    }

    this.checkExistingFilesAndUpload();
  }

  private checkExistingFilesAndUpload = (): void => {
    const checkExisting$ = this.listApiService.checkExistingFiles(this.files4upload.map(f => f.name))
      .pipe(
        catchError((err) => this.errorHandler.handleError(err)),
        share(),
        takeUntil(this.ngOnDestroy$));

    const fileExists$ = checkExisting$.pipe(
      switchMap((value) => value.length > 0 ? of(value) : EMPTY),
      mergeMap(items => items),
      concatMap(s => defer(() => new Promise<IDialogOnClose>((resolve, reject) => {
        this.currentResolve = resolve;
        this.currentReject = reject;
        this.showAlert(s);
      }))),
      tap((result) => {
        const file = this.files4upload.find(f => f.name === result.data.fileName);
        if (result.data.operation !== FileOperation.SKIP) {
          this.fmUploader.filesUploadQueue.push({
            file: file,
            keep: result.data.operation === FileOperation.KEEPBOTH
          });
        } else {
          this.fmUploader.errors.push(<FileUploadError>{
            name: file.name,
            size: file.size.toString(),
            reason: 'skipped'
          });
        }
      }),
      takeUntil(this.ngOnDestroy$)
    );

    const fileNotExists$ = checkExisting$.pipe(
      switchMap((value) => value.length < this.files4upload.length ? of(value) : EMPTY),
      map((value) => this.files4upload.filter(f => !value.some(v => v === f.name))),
      map((files) => files.map(f => <FileUploadInfo>{ file: f, keep: false })),
      tap((files) => this.fmUploader.filesUploadQueue.push(...files)),
      takeUntil(this.ngOnDestroy$)
    );

    merge(fileExists$, fileNotExists$)
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe({
        next: () => { },
        error: () => { 
          this.fmUploader.clear();
        },
        complete: () => {
          this.fmUploader.parentfolder = this.currentFolder;
          this.fmUploader.upload(this.dialogType, this.fileListPageSize);
          this.files4upload.length = 0;
          this.applyAllOperation = null;
        }
      });
  }

  private showAlert = (fileName: string) => {
    if (this.applyAllOperation) {
      const cloned = Object.assign(<IDialogOnClose>{}, this.applyAllOperation);
      cloned.data.fileName = fileName;
      this.currentResolve(cloned);
      return;
    }
    this.dialogService.createDialog(AlertDublicateFileComponent, { fileName, dialogType: this.dialogType }, {})
      .onClose
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe((result) => {
        if (result.reason === DialogCloseReason.OK) {
          if (result.data.applyAll) {
            this.applyAllOperation = result;
          }
          this.currentResolve(result);
        } else {
          this.currentReject(result);
        }
      });
  }

  private validate = (file: File): boolean => {
    if (this.accept && !this.isFileTypeValid(file)) {
      this.fmUploader.errors.push({
        name: file.name,
        reason: 'Invalid file type',
        size: file.size.toString()
      });
      return false;
    }

    if (this.maxFileSize && file.size > this.maxFileSize) {
      let maxFileSizeError: string;
      if (this.dialogType === FileModel.FileTypeEnum.IMAGES) {
        maxFileSizeError = this.translate.instant('COMMON.DIALOGS.FILE_MANAGER.MAX_FILE_SIZE_ERROR');
      } else {
        maxFileSizeError = this.translate.instant('COMMON.DIALOGS.FILE_MANAGER.MAX_FILE_FILES_SIZE_ERROR');
      }
      this.fmUploader.errors.push({
        name: file.name,
        reason: maxFileSizeError,
        size: file.size.toString()
      });
      return false;
    }

    return true;
  }

  private isFileTypeValid = (file: File): boolean => {
    let acceptableTypes: string[];
    if (this.dialogType === FileModel.FileTypeEnum.IMAGES) {
      acceptableTypes = this.accept.split(',').map(type => type.trim());
    }
    if (this.dialogType === FileModel.FileTypeEnum.DOCUMENTS) {
      return true;
    }

    for (const type of acceptableTypes) {
      const acceptable = this.isWildcard(type) ? this.getTypeClass(file.type) === this.getTypeClass(type)
        : file.type === type || this.getFileExtension(file).toLowerCase() === type.toLowerCase();

      if (acceptable) {
        return true;
      }
    }
    return false;
  }

  private getTypeClass = (fileType: string): string => fileType.substring(0, fileType.indexOf('/'));

  private isWildcard = (fileType: string): boolean => fileType.indexOf('*') !== -1;

  private getFileExtension = (file: File): string => `.${file.name.split('.').pop()}`;

}
